import React, { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Icon } from '@/shared/icons'
/**
 * Form for connecting Email SMTP
 *
 * Flow: SMTP configuration → Test connection → Complete
 *
 * @example
 * <EmailSMTPForm
 *   onComplete={(data) => handleAuthComplete(data)}
 * />
 */
export interface EmailSMTPFormProps {
  /** Callback when authentication is complete */
  onComplete: (data: EmailSMTPData) => void;
}

export interface EmailSMTPData {
  providerId: string;
  name: string;
  identifier: string;
  config: {
    host: string;
    port: number;
    secure: boolean;
    username: string;
    password: string;
    fromEmail: string;
    fromName: string;
  };
}

type ConnectionStep = 'configuration' | 'testing' | 'complete';

export const EmailSMTPForm: React.FC<EmailSMTPFormProps> = ({
  onComplete,
}) => {
  const [step, setStep] = useState<ConnectionStep>('configuration');
  const [channelName, setChannelName] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('587');
  const [secure, setSecure] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleTestConnection = async () => {
    // ✅ Validação completa conforme backend
    if (!channelName || !host || !port || !username || !password || !fromEmail) {
      setErrorMessage('Preencha todos os campos obrigatórios');
      return;
    }

    if (channelName.trim().length < 2) {
      setErrorMessage('Nome do canal deve ter pelo menos 2 caracteres');
      return;
    }

    // Validação de email básica
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(fromEmail)) {
      setErrorMessage('E-mail "De" inválido');
      return;
    }

    setErrorMessage(null);
    setStep('testing');

    // TODO: Call backend API to test SMTP connection
    setTimeout(() => {
      setStep('complete');
    }, 2000);
  };

  const handleComplete = () => {
    onComplete({
      providerId: 'email-smtp',
      name: channelName,
      identifier: fromEmail,
      config: {
        host,
        port: parseInt(port),
        secure,
        username,
        password,
        fromEmail,
        fromName: fromName || channelName,
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Step 1: SMTP Configuration */}
      {step === 'configuration' && (
        <div className="space-y-4">
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Nome do Canal *
            </label>
            <Input
              type="text"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              placeholder="Ex: E-mail Suporte"
              className="w-full"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Servidor SMTP *
              </label>
              <div className="relative">
                <Icon name="server" size={18}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none"
                  style={{ color: 'var(--theme-card-muted)' }} />
                <Input
                  type="text"
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  placeholder="smtp.gmail.com"
                  className="w-full pl-10"
                />
              </div>
            </div>

            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Porta *
              </label>
              <Input
                type="number"
                value={port}
                onChange={(e) => setPort(e.target.value)}
                placeholder="587"
                className="w-full"
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={secure}
                onChange={(e) => setSecure(e.target.checked)}
                className="w-4 h-4 rounded"
                style={{
                  accentColor: 'var(--theme-button-primary)',
                }}
              />
              <span
                className="text-sm"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Usar conexão segura (TLS/SSL)
              </span>
            </label>
            <p
              className="text-xs mt-1 ml-6"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              Recomendado para portas 465 (SSL) e 587 (TLS)
            </p>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Usuário/E-mail *
            </label>
            <div className="relative">
              <Icon name="mail" size={18}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none"
                style={{ color: 'var(--theme-card-muted)' }} />
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="usuario@exemplo.com"
                className="w-full pl-10"
              />
            </div>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Senha *
            </label>
            <div className="relative">
              <Icon name="lock" size={18}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none"
                style={{ color: 'var(--theme-card-muted)' }} />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full pl-10"
              />
            </div>
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              Use uma senha de aplicativo se disponível (ex: Gmail App Password)
            </p>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              E-mail Remetente *
            </label>
            <Input
              type="email"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="suporte@empresa.com"
              className="w-full"
            />
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              E-mail que aparecerá como remetente
            </p>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Nome do Remetente
            </label>
            <Input
              type="text"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="Equipe de Suporte"
              className="w-full"
            />
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              Opcional - Nome que aparecerá como remetente
            </p>
          </div>

          {/* Common SMTP providers info */}
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: 'var(--theme-info-background)',
              border: '1px solid var(--theme-info-color)',
            }}
          >
            <p
              className="text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Configurações comuns:
            </p>
            <div
              className="text-xs space-y-1"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              <p><strong>Gmail:</strong> smtp.gmail.com:587 (TLS)</p>
              <p><strong>Outlook:</strong> smtp-mail.outlook.com:587 (TLS)</p>
              <p><strong>Office 365:</strong> smtp.office365.com:587 (TLS)</p>
              <p><strong>Yahoo:</strong> smtp.mail.yahoo.com:465 (SSL)</p>
            </div>
          </div>

          {errorMessage && (
            <div
              className="p-3 rounded-lg"
              style={{
                backgroundColor: 'var(--theme-error-background)',
                border: '1px solid var(--theme-error-color)',
              }}
            >
              <p
                className="text-sm"
                style={{ color: 'var(--theme-error-color)' }}
              >
                {errorMessage}
              </p>
            </div>
          )}

          <Button
            onClick={handleTestConnection}
            className="w-full"
          >
            Testar Conexão
          </Button>
        </div>
      )}

      {/* Step 2: Testing Connection */}
      {step === 'testing' && (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center mb-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center animate-pulse"
              style={{ backgroundColor: 'var(--theme-button-primary)' }}
            >
              <Icon name="mail" size={32} className="text-white" />
            </div>
          </div>
          <p
            className="text-lg font-medium"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Testando conexão SMTP...
          </p>
          <p
            className="text-sm mt-2"
            style={{ color: 'var(--theme-card-muted)' }}
          >
            Verificando credenciais e enviando e-mail de teste
          </p>
        </div>
      )}

      {/* Step 3: Connection Successful */}
      {step === 'complete' && (
        <div className="text-center py-8">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
            style={{ backgroundColor: 'var(--theme-success-color)' }}
          >
            <Icon name="check-circle" size={32} className="text-white" />
          </div>
          <p
            className="text-lg font-medium mb-2"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Conexão estabelecida com sucesso!
          </p>
          <p
            className="text-sm mb-6"
            style={{ color: 'var(--theme-card-muted)' }}
          >
            Servidor SMTP configurado e pronto para enviar e-mails
          </p>
          <Button
            onClick={handleComplete}
            className="w-full"
          >
            Concluir Configuração
          </Button>
        </div>
      )}
    </div>
  );
};

EmailSMTPForm.displayName = 'EmailSMTPForm';
